import { Upload, Music, Film, Download } from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: Upload,
    title: "Share Your Photos",
    body: "Tell us about your loved one and you'll instantly get a private upload link. Phone snapshots and old scans are all welcome — add them a few at a time.",
  },
  {
    n: "02",
    icon: Music,
    title: "Choose Music & Style",
    body: "Pick a song that was meaningful to them, or let us set the mood with beautiful licensed cinematic music. Choose Celebration, Legacy, or a blend.",
  },
  {
    n: "03",
    icon: Film,
    title: "We Craft Your Film",
    body: "Our editors hand-build your tribute: cinematic motion and transitions, custom title cards with their name and dates, and a final gentle color grade.",
  },
  {
    n: "04",
    icon: Download,
    title: "Receive & Share",
    body: "Receive your film in HD digitally, with an optional USB keepsake. Play it at the service, share it with family — anywhere in the world.",
  },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="relative py-24 sm:py-32 overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[rgba(212,175,55,0.3)] to-transparent" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <div className="overline mb-4">The Process</div>
          <h2 className="serif text-4xl sm:text-5xl lg:text-[56px] font-light leading-[1.05] text-[#F5F0E8]">
            From your photos to a film,
            <span className="italic text-[#E6B89C]"> gently.</span>
          </h2>
          <p className="mt-5 text-[#A0988E] spectral max-w-xl leading-relaxed">
            You handle the memories — we handle everything else. Four simple steps,
            guided with care at every turn.
          </p>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.n}
                data-testid={`process-step-${i + 1}`}
                className="card-surface p-7 relative group hover:border-[rgba(212,175,55,0.5)] transition-colors"
                style={{ animationDelay: `${i * 90}ms` }}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="serif text-[48px] leading-none text-[#C5A55A]/40 font-light">{s.n}</span>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.06)]">
                    <Icon size={18} className="text-[#D4AF37]" strokeWidth={1.4} />
                  </span>
                </div>
                <h3 className="serif text-[22px] text-[#F5F0E8] mb-3 leading-snug">{s.title}</h3>
                <p className="text-[14.5px] text-[#A0988E] leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-center">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full border border-[rgba(212,175,55,0.25)] bg-[rgba(20,19,17,0.5)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] glow-pulse" />
            <span className="text-sm text-[#A0988E]">
              Typical delivery <span className="text-[#F5F0E8]">5–7 days</span> — rush available when the service can't wait.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
